'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Spinner } from '@common/components';
import { Lottie } from '@common/components/atom/Lottie';
import { useSuspenseQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { useRef } from 'react';
import { UnauthQueryOptions } from '@/api';
import { Image } from '@/components/client';
import { ROUTES, WELLO_IMAGE_PREFIX_URL } from '@/constants';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import type { ChatbotAnswers } from '../_lib/types';
import { PolicyCard } from './PolicyCard';
interface ResultStepProps {
  answers: ChatbotAnswers;
  onRestart?: () => void;
}
export const ResultStep = t0 => {
  const $ = _c(49);
  const {
    answers,
    onRestart
  } = t0;
  const prefersReducedMotion = useReducedMotion();
  const scrollContainerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      scrollContainerRef.current?.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth"
      });
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const scrollToBottom = t1;
  let t2;
  if ($[1] !== answers.age || $[2] !== answers.economic || $[3] !== answers.hasChild || $[4] !== answers.job) {
    t2 = UnauthQueryOptions["get:/wello/v2/allows/non-member-suggest-policy"]({
      query: {
        nonMemberAgeType: answers.age as 'Y' | 'M' | 'S',
        nonMemberJobType: answers.job as 'E' | 'B',
        nonMemberSituationType: answers.economic as 'G' | 'V',
        nonMemberChildType: answers.age === "S" ? undefined : answers.hasChild as 'C' | 'N' ?? "N"
      }
    });
    $[1] = answers.age;
    $[2] = answers.economic;
    $[3] = answers.hasChild;
    $[4] = answers.job;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = {
      ...t2
    };
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    data,
    isLoading
  } = useSuspenseQuery(t3);
  let t4;
  if ($[8] !== data?.context) {
    t4 = data?.context ?? [];
    $[8] = data?.context;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const recommendations = t4;
  if (isLoading) {
    let t5;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="mx-auto flex min-h-dvh w-full max-w-screen-xl items-center justify-center"><Spinner /></div>;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      background: "linear-gradient(to bottom, rgb(221, 242, 255) 0px, rgb(249, 249, 249) 300px, rgb(249, 249, 249))"
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      opacity: 1,
      y: 0
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      opacity: 0,
      y: 20
    };
    t8 = {
      duration: 0.4
    };
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  let t9;
  if ($[15] !== prefersReducedMotion) {
    t9 = !prefersReducedMotion && <Lottie loop={false} src="/assets/jsons/flex-confetti.json" className="pointer-events-none absolute inset-x-0 top-0 h-300" />;
    $[15] = prefersReducedMotion;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      scale: 1
    };
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <motion.div animate={t10} className="relative mb-16 flex justify-center" initial={{
      scale: 0
    }} transition={{
      delay: 0.2,
      type: "spring"
    }}><Image src={`${WELLO_IMAGE_PREFIX_URL}/static/fit-policy.png`} alt={"\uB9DE\uCDA4 \uC815\uCC45"} width={157} height={89} /></motion.div>;
    t12 = <h2 className="mb-12 text-center font-700 text-Gray-900 typo-h6">맞춤 정책을 찾았어요!</h2>;
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  let t13;
  if ($[20] !== recommendations.length) {
    t13 = <span className="text-B-500">{recommendations.length}개의 정책</span>;
    $[20] = recommendations.length;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <br />;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t13) {
    t15 = <p className="text-center leading-normal text-Gray-800 typo-body4">내가 받을 수 있는{" "}{t13}{" "}중 일부를 보여드려요{t14}내게 맞는 정책을 매일 알림받으려면 무료로 가입해보세요!</p>;
    $[23] = t13;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t15 || $[26] !== t9) {
    t16 = <motion.div animate={t6} className="relative px-20 pb-24 pt-40" initial={t7} transition={t8}>{t9}{t11}{t12}{t15}</motion.div>;
    $[25] = t15;
    $[26] = t9;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== recommendations) {
    let t18;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = (policy, index) => <motion.div key={policy.metaPolicyId ?? `policy-${index}`} animate={{
        opacity: 1,
        y: 0
      }} initial={{
        opacity: 0,
        y: 20
      }} transition={{
        delay: index * 0.1
      }}><PolicyCard index={index} policy={policy} onClick={scrollToBottom} /></motion.div>;
      $[30] = t18;
    } else {
      t18 = $[30];
    }
    t17 = recommendations.map(t18);
    $[28] = recommendations;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[31] !== t17) {
    t18 = <div className="flex flex-col gap-10 px-20">{t17}</div>;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  let t20;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="mb-4 flex items-center gap-4"><div className="size-20 rounded-pill bg-Gray-200" /><span className="text-Gray-500 typo-caption1">기관명</span></div>;
    t20 = <p className="mb-16 font-600 text-Gray-black typo-body3">정책명이 여기에 표시됩니다</p>;
    $[33] = t19;
    $[34] = t20;
  } else {
    t19 = $[33];
    t20 = $[34];
  }
  let t21;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className="px-24 py-28 opacity-30">{t19}{t20}<div className="rounded-small bg-Gray-25 p-12"><div className="mb-4 flex items-center gap-2"><span className="font-700 text-B-500 typo-caption1">AI 요약</span></div><div className="h-15 rounded-xsmall bg-Gray-200" /></div></div>;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="absolute inset-0 backdrop-blur-sm" style={{
      background: "linear-gradient(180deg, rgba(255, 255, 255, 0.6) 0%, #F6F6F6 62%)"
    }} />;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <p className="mb-8 font-700 text-B-500 typo-body1">??개의 맞춤 정책이 더 있어요</p>;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className="h-137 overflow-hidden px-20 pt-10"><div className="relative overflow-hidden rounded-xxlarge bg-Gray-white">{t21}{t22}<div className="absolute inset-0 flex flex-col items-center justify-center px-20 pb-30 text-center">{t23}<p className="text-Gray-black typo-body4">웰로 회원들은 평균 100개의 맞춤정책을<br />조회할 수 있어요</p></div></div></div>;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Button asChild={true} className="w-full" theme="secondary-fill"><Link href={ROUTES.LOGIN.withSearchParams({
        searchParams: {
          redirect: ROUTES.RECOMMEND_POLICY_LIST.pathname
        }
      })}>맞춤 정책 더 보러가기</Link></Button>;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  if ($[40] !== onRestart) {
    t26 = <Button className="w-full" dimensions="H40" theme="primary-text" onClick={onRestart}>다시 찾아보기</Button>;
    $[40] = onRestart;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  let t27;
  if ($[42] !== t25 || $[43] !== t26) {
    t27 = <div className="flex flex-col gap-8 bg-Gray-50 px-20 py-12">{t25}{t26}</div>;
    $[42] = t25;
    $[43] = t26;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] !== t16 || $[46] !== t18 || $[47] !== t27) {
    t28 = <div className="mx-auto flex min-h-dvh w-full max-w-screen-xl flex-col" style={t5}><div ref={scrollContainerRef} className="flex-1 overflow-y-auto">{t16}{t18}{t24}{t27}</div></div>;
    $[45] = t16;
    $[46] = t18;
    $[47] = t27;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  return t28;
};